import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { auctioneerDataMatrixItemType } from '../../types';
import NumberField from '../fields/NumberField';
import useExtendedState from '../../hooks/useExtendedState';
import './_styles_/AuctioneerDataMatrixRow.css';
import Constants from '../../utils/Constants';
import useEffectOnUpdate from '../../hooks/useEffectOnUpdate';
import useSingleValidator from '../../hooks/useSingleValidator';
import { auctioneerDataMatrixQuantityValidator } from '../../validators/auctioneerDataMatrix';
import Number from '../Number';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

AuctioneerDataMatrixRow.propTypes = {
    isModifiable: PropTypes.bool.isRequired,
    item: auctioneerDataMatrixItemType.isRequired,
    onRemoveItem: PropTypes.func.isRequired,
    onQuantityChange: PropTypes.func.isRequired,
    quantityMultiple: PropTypes.number.isRequired,
    maxQuantity: PropTypes.number.isRequired,
    doReset: PropTypes.bool.isRequired,
    doResetComponentsAfterModification: PropTypes.bool.isRequired,
    doAdjustValuesAfterTemplateApplied: PropTypes.bool.isRequired,
    doAdjustValuesAfterExternalUpdate: PropTypes.bool.isRequired,
};

function AuctioneerDataMatrixRow(props) {
    const {
        isModifiable,
        item,
        onRemoveItem,
        onQuantityChange,
        quantityMultiple,
        maxQuantity,
        doReset,
        doResetComponentsAfterModification,
        doAdjustValuesAfterTemplateApplied,
        doAdjustValuesAfterExternalUpdate,
    } = props;
    const { t } = useTranslation();
    const quantityStep = quantityMultiple || 1;
    const quantityState = useExtendedState(item.quantity);
    const { validator: quantityValidator, reset: resetQuantityValidator } = useSingleValidator(false, useMemo(() => compositeValidator(auctioneerDataMatrixQuantityValidator(t, Constants.MIN_AUCTIONEER_DATA_QUANTITY, maxQuantity, quantityMultiple), wafValidator(t)), [t, maxQuantity, quantityMultiple]));
    const changeQuantity = (newQuantity) => {
        if (quantityState.value !== newQuantity) {
            onQuantityChange(newQuantity);
            quantityState.setValue(newQuantity);
        }
    };
    useEffectOnUpdate(() => {
        quantityState.reset();
        resetQuantityValidator();
    }, [doReset]);
    useEffectOnUpdate(() => {
        if (doAdjustValuesAfterTemplateApplied) {
            quantityState.setValue(item.quantity);
        }
    }, [item, item.quantity, doAdjustValuesAfterTemplateApplied]);
    useEffectOnUpdate(() => {
        if (doAdjustValuesAfterExternalUpdate) {
            quantityState.changeInitialState(item.quantity);
            quantityState.reset();
            resetQuantityValidator();
        }
    }, [item, item.quantity, doAdjustValuesAfterExternalUpdate]);
    useEffectOnUpdate(() => {
        if (doResetComponentsAfterModification) {
            quantityState.changeInitialState(quantityState.value);
            quantityState.reset();
            resetQuantityValidator();
        }
    }, [doResetComponentsAfterModification]);
    return (
        <div className="detail-list-row">
            {isModifiable && (
                <div className="auctioneer-data-matrix-cell--small auctioneer-data-matrix-row--remove">
                    <button
                        type="button"
                        className="remove-button button-small button-bordered"
                        onClick={onRemoveItem}
                    >
                        <span aria-label={t('auctioneer-data-matrix.remove-item')} title={t('auctioneer-data-matrix.remove-item')} role="img">
                            <FontAwesomeIcon icon="times" />
                        </span>
                    </button>
                </div>
            )}
            <div className="auctioneer-data-matrix-cell auctioneer-data-matrix-row--member-name">
                {item.auctioneer.name}
            </div>
            <div className="auctioneer-data-matrix-cell auctioneer-data-matrix-row--member-id">
                {item.auctioneer.memberId}
            </div>
            <div className="auctioneer-data-matrix-cell auctioneer-data-matrix-row--submarket-name">
                {item.submarket.name}
            </div>
            <div className="auctioneer-data-matrix-cell auctioneer-data-matrix-row--submarket-code">
                {item.submarket.code}
            </div>
            <div className="auctioneer-data-matrix-cell auctioneer-data-matrix-row--quantity">
                {isModifiable ? (
                    <NumberField
                        state={quantityState}
                        className="auctioneer-data-matrix-row--quantity--value"
                        onChange={changeQuantity}
                        min={Constants.MIN_AUCTIONEER_DATA_QUANTITY}
                        max={maxQuantity}
                        validator={quantityValidator}
                        step={quantityStep}
                        decrementDisabled={quantityState.value - quantityStep < Constants.MIN_AUCTIONEER_DATA_QUANTITY}
                        incrementDisabled={quantityState.value + quantityStep > maxQuantity}
                        validateOnLoad
                        validateOnValidatorChange
                        roundToStepMultiple
                    />
                ) : (
                    <Number value={quantityState.value} />
                )}
            </div>
        </div>
    );
}

export default memo(AuctioneerDataMatrixRow);
